import React, { useState } from "react";
import { FaPencilAlt } from "react-icons/fa";
import { PreferredLocale } from "../../../utils/locales";
import { HTMLEditor } from "./Editor.container";
import { EditButton, EditPlaceholder, HTMLContentContainer } from "./HTMLContent.style";
import { HTMLRenderer } from "./Renderer";

export interface IHTMLContentProps {
  content?: string;
  disabled?: boolean;
  locale: PreferredLocale;
  isOpened?: boolean;
  onChange?: (content: string) => void;
  onSubmit: (content: string, currentLocale?: PreferredLocale) => void;
  placeholder?: string;
}

interface IHTMLContentState {
  opened: boolean;
  content: string;
  showEdit: boolean;
}

export const HTMLContent: React.FC<IHTMLContentProps> = props => {
  const {
    content = "",
    disabled,
    isOpened = false,
    locale,
    onChange,
    onSubmit,
    placeholder = "Click here to edit...",
  } = props;

  const [state, setState] = useState<IHTMLContentState>({
    content,
    opened: isOpened,
    showEdit: false,
  });

  const showEditor = () => {
    if (!disabled) {
      setState({ ...state, opened: true, showEdit: false });
    }
  };

  const hideEditor = () => {
    setState({ ...state, opened: false });
  };

  const getContent = () => state.content;

  const setContent = (htmlContent: string, currentLocale?: PreferredLocale) => {
    if (htmlContent !== content) {
      onSubmit(htmlContent, currentLocale);
    }
    hideEditor();
  };

  const onContentChange = (value: string) => {
    setState({ ...state, content: value });

    if (onChange) {
      onChange(value);
    }
  };

  const toggleEdit = (showEdit: boolean) => () => setState({ ...state, showEdit });

  const containerRef = React.useRef() as React.RefObject<HTMLDivElement>;

  if (state.opened) {
    return (
      <HTMLContentContainer ref={containerRef}>
        <HTMLEditor
          {...props}
          getContent={getContent}
          initialContent={content}
          setContent={setContent}
          currentLocale={locale}
          onContentChange={onContentChange}
          shouldAutoFocus={true}
          containerRef={containerRef}
        />
      </HTMLContentContainer>
    );
  }

  if (disabled) {
    return <HTMLRenderer {...props} source={state.content} />;
  }

  return (
    <HTMLContentContainer onMouseEnter={toggleEdit(true)} onMouseLeave={toggleEdit(false)}>
      {state.showEdit && (
        <EditButton onClick={showEditor}>
          <FaPencilAlt />
        </EditButton>
      )}
      {state.content === "" ? (
        <EditPlaceholder>{placeholder}</EditPlaceholder>
      ) : (
        <HTMLRenderer {...props} source={state.content} />
      )}
    </HTMLContentContainer>
  );
};
